/*
 * Calculadora nutricional inteligente.
 * Wizard de 6 pasos + cálculo Mifflin-St Jeor + recomendaciones + add-to-cart.
 * La matemática vive en computePlan() (pura). Los coeficientes se leen del config island
 * (ajustes de la sección) con fallback a DEFAULT_CONFIG.
 */
if (!customElements.get('nutrition-calculator')) {
  // Clave de sesión: las respuestas sobreviven a un refresh de la página
  const STORAGE_KEY = 'nc-plan-v1';

  // Coeficientes canónicos (closure, definidos ANTES de define() para que
  // connectedCallback/readConfig los tengan disponibles al hacer upgrade síncrono).
  // Modelo por kg de peso — Requerimientos Nutricionales de Referencia (avalado ISSN/ACSM/IOC).
  // Cada nutriente usa el RANGO del documento; la posición dentro del rango la da el nivel de
  // actividad (sedentario = extremo bajo … atleta = extremo alto), para personalizar sin salir
  // de los valores avalados. Grasa = calorías restantes. Todo escala con el peso corporal.
  const DEFAULT_CONFIG = {
    /* --- Factor de actividad: puntaje continuo, asignado por el algoritmo --- */
    // El usuario NUNCA elige el factor. Se parte de un piso según la actividad
    // cotidiana y se suma la carga de entreno (días × minutos por sesión), con
    // rendimiento decreciente. El resultado se acerca al factor canónico más
    // próximo, de modo que el nivel resultante siga sirviendo para recomendar.
    // Bases calibradas para reproducir los 10 anclajes del documento
    // (tabla 2.2 + los 2 casos de 2.3) manteniendo monotonía en días y duración.
    activityBase: { sentado: 1.2, mixta: 1.29, de_pie: 1.31, fisico: 1.5 },
    durationMid: { menos_30: 20, '30_60': 45, '60_90': 75, mas_90: 105 },
    loadA: 0.875, loadC: 360,
    canonFactors: [1.2, 1.375, 1.55, 1.725, 1.9],
    canonLevels: ['sedentario', 'ligero', 'moderado', 'alto', 'atleta'],

    /* --- Ajuste por objetivo --- */
    deficitPct: 0.15, deficitMin: 250, deficitMax: 600,
    surplusPct: { principiante: 0.1, intermedio: 0.075, avanzado: 0.05 },
    surplusMin: 150, surplusMax: 350,

    /* --- Macronutrientes (g/kg) --- */
    proteinLose: 2.0,
    proteinMaintain: { fuerza: 1.7, mixto: 1.7, cardio: 1.5, deporte: 1.5 },
    proteinGain: { principiante: 1.8, intermedio: 1.9, avanzado: 2.0 },
    fatPerKg: 0.8,

    /* --- Creatina: solo con estímulo de fuerza o en volumen --- */
    // 0.1 g/kg acotado a [3, 10] (pedido de Oscar, 2026-08-04; reemplaza el
    // 0.03 g/kg con tope 5 de la especificación funcional original).
    creatinePerKg: 0.1, creatineMin: 3, creatineMax: 10,
    creatineTypes: ['fuerza', 'mixto'], creatineGoals: ['ganar_musculo'],

    /* --- Reparto calórico del día según cuántas comidas hace la persona --- */
    // Porcentaje de las calorías diarias por franja. Tablas provistas por el
    // cliente; cada escenario suma 100%. Las franjas sin porcentaje no se muestran.
    mealSplits: {
      3: { desayuno: 30, almuerzo: 40, cena: 30 },
      4: { desayuno: 25, media_manana: 15, almuerzo: 35, cena: 25 },
      5: { desayuno: 25, media_manana: 10, almuerzo: 30, media_tarde: 10, cena: 25 },
      6: { desayuno: 20, media_manana: 10, almuerzo: 25, media_tarde: 10, cena: 25, post: 10 },
    },
    mealOrder: ['desayuno', 'media_manana', 'almuerzo', 'media_tarde', 'cena', 'post'],
    defaultMeals: 4,

    /* --- Hidratación: base por kg + adicional por duración de sesión --- */
    hydrationMlPerKg: 35,
    hydrationBonusMl: { menos_30: 0, '30_60': 400, '60_90': 700, mas_90: 1000 },

    /* --- Redondeo --- */
    kcalRoundTo: 10,
    maxRecommendations: 4,
  };

  customElements.define(
    'nutrition-calculator',
    class NutritionCalculator extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.addIcon = this.dataset.addIcon || '';
        this.config = this.readConfig();
        this.products = this.readProducts();

        this.steps = Array.from(this.querySelectorAll('[data-nc-step]'));
        this.tabs = Array.from(this.querySelectorAll('[data-nc-tab]'));
        this.progress = this.querySelector('[data-nc-progress]');
        this.prevBtn = this.querySelector('[data-nc-prev]');
        this.nextBtn = this.querySelector('[data-nc-next]');
        this.nextLabel = this.querySelector('[data-nc-next-label]');

        this.TOTAL = this.steps.length;          // 6
        this.PLAN_STEP = this.TOTAL - 1;          // 5
        this.maxReached = 0;

        this.state = {
          step: 0,
          sex: null, age: null,
          weight_kg: null, height_cm: null,
          // activity = actividad COTIDIANA (sin contar entrenamientos)
          activity: null, frequency_per_week: null, duration: null,
          training_type: null, experience: null,
          goal: null,
          // meals: preseleccionado en el liquid (botón con is-selected); mantener en sync
          schedule: null, meals: '4', restrictions: [],
        };

        // Inicializar valores de los sliders desde el DOM
        this.querySelectorAll('[data-nc-range]').forEach((r) => {
          this.state[r.dataset.ncField] = parseFloat(r.value);
          this.syncOutput(r.dataset.ncField, r.value);
        });

        this.bindEvents();
        // Recuperar la sesión antes de pintar, para abrir donde se quedó
        const restored = this.restore();
        this.updateLiveStats();
        this.toggleTrainingFields();
        this.goTo(restored ? this._restoredStep : 0, { focus: false });
      }

      /* ---------- lectura de islands ---------- */
      readConfig() {
        const D = DEFAULT_CONFIG;
        let raw = {};
        const el = this.querySelector('[data-nc-config]');
        try { raw = JSON.parse(el.textContent); } catch (e) { raw = {}; }
        const n = (v, d) => { const x = parseFloat(v); return Number.isFinite(x) ? x : d; };
        // El modelo (Mifflin-St Jeor + factor por puntaje) vive en DEFAULT_CONFIG.
        // De los ajustes de la sección se leen los coeficientes que el negocio puede tunear.
        return Object.assign({}, D, {
          maxRecommendations: Math.max(1, Math.round(n(raw.maxRecommendations, D.maxRecommendations))),
          deficitPct: n(raw.deficitPct, D.deficitPct),
          deficitMin: n(raw.deficitMin, D.deficitMin),
          deficitMax: n(raw.deficitMax, D.deficitMax),
          surplusMin: n(raw.surplusMin, D.surplusMin),
          surplusMax: n(raw.surplusMax, D.surplusMax),
          fatPerKg: n(raw.fatPerKg, D.fatPerKg),
          hydrationMlPerKg: n(raw.hydrationMlPerKg, D.hydrationMlPerKg),
          creatinePerKg: n(raw.creatinePerKg, D.creatinePerKg),
          proteinLose: n(raw.proteinLose, D.proteinLose),
          surplusPct: Object.assign({}, D.surplusPct, {
            principiante: n(raw.surplusPrincipiante, D.surplusPct.principiante),
            intermedio: n(raw.surplusIntermedio, D.surplusPct.intermedio),
            avanzado: n(raw.surplusAvanzado, D.surplusPct.avanzado),
          }),
        });
      }

      readProducts() {
        const el = this.querySelector('[data-nc-products]');
        if (!el) return [];
        let arr = [];
        try { arr = JSON.parse(el.textContent); } catch (e) { return []; }
        const list = (v) => (Array.isArray(v) ? v : []);
        return (arr || []).map((p) => {
          // Variantes disponibles; por defecto queda seleccionada la más cara,
          // que es la presentación que el negocio quiere empujar.
          const variants = list(p.variants).filter((v) => v.available !== false);
          const defaultVariant = variants.length
            ? variants.reduce((a, b) => ((b.price_num || 0) > (a.price_num || 0) ? b : a))
            : null;
          return {
          ...p,
          variants,
          selected_variant_id: defaultVariant ? defaultVariant.id : p.variant_id,
          selected_price: defaultVariant ? defaultVariant.price : p.price,
          objetivo: list(p.objetivo),
          restricciones: list(p.restricciones),
          // Ejes del mapa de productos (Parte 3). Vacío = aplica a todos.
          sexo: list(p.sexo), edad: list(p.edad), imc: list(p.imc),
          nivel: list(p.nivel), tipo: list(p.tipo), horario: list(p.horario),
          prioridad: Number.isFinite(parseInt(p.prioridad, 10)) ? parseInt(p.prioridad, 10) : 99,
          catRaw: p.categoria || '', // categoría real (para el "por qué")
          // 'otro'/vacío -> categoría única por producto (no se agrupan en el cap de diversidad)
          categoria: (p.categoria && p.categoria !== 'otro') ? p.categoria : ('u' + p.variant_id),
          };
        });
      }

      /* ---------- eventos ---------- */
      bindEvents() {
        this.addEventListener('click', (e) => {
          const opt = e.target.closest('[data-nc-field][data-nc-value]');
          if (opt) { this.onSelect(opt); return; }
          const tab = e.target.closest('[data-nc-tab]');
          if (tab) { this.onTab(parseInt(tab.dataset.ncTab, 10)); return; }
          const addBtn = e.target.closest('.cross-selling__add-btn');
          if (addBtn) { this.onAddToCart(addBtn); return; }
          const buyAll = e.target.closest('[data-nc-buy-all]');
          if (buyAll) { this.onBuyAll(buyAll); return; }
        });

        // Selector de variante en las recomendaciones
        this.addEventListener('change', (e) => {
          const v = e.target.closest('[data-nc-variant]');
          if (v) this.onVariantChange(v);
        });

        this.querySelectorAll('[data-nc-range]').forEach((r) => {
          r.addEventListener('input', () => {
            this.state[r.dataset.ncField] = parseFloat(r.value);
            this.syncOutput(r.dataset.ncField, r.value);
            this.updateLiveStats();
            if (r.dataset.ncField === 'frequency_per_week') this.toggleTrainingFields();
            this.persist();
          });
        });

        this.nextBtn.addEventListener('click', () => this.onNext());
        this.prevBtn.addEventListener('click', () => this.goTo(this.state.step - 1));
        this.querySelector('[data-nc-recalc]').addEventListener('click', () => this.goTo(0));

        const back = this.querySelector('[data-nc-back]');
        if (back) {
          back.addEventListener('click', (e) => {
            // Dentro del modal: solo cerrarlo, nunca navegar
            if (this.closest('.nc-modal__dialog')) {
              e.preventDefault();
              e.stopImmediatePropagation();
              document.dispatchEvent(new CustomEvent('nutrition-calc:close-modal'));
              return;
            }
            const sameOrigin = document.referrer && document.referrer.indexOf(window.location.origin) === 0;
            if (sameOrigin && window.history.length > 1) { e.preventDefault(); window.history.back(); }
          });
        }
      }

      onSelect(el) {
        const field = el.dataset.ncField;
        const value = el.dataset.ncValue;
        if (el.hasAttribute('data-nc-multi')) {
          const arr = this.state[field];
          const i = arr.indexOf(value);
          if (i === -1) arr.push(value); else arr.splice(i, 1);
          el.classList.toggle('is-selected', i === -1);
          el.setAttribute('aria-pressed', i === -1 ? 'true' : 'false');
        } else {
          this.state[field] = value;
          this.querySelectorAll('[data-nc-field="' + field + '"]').forEach((sib) => {
            const on = sib === el;
            sib.classList.toggle('is-selected', on);
            sib.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          this.clearInvalid(field);
        }
        this.persist();
      }

      /* ---------- persistencia en la sesión ---------- */
      // Las respuestas sobreviven a un refresh (sessionStorage, no localStorage:
      // el plan es de esta visita). Se comparte entre la página y el modal del
      // header, que son dos instancias del mismo componente.
      persist() {
        try {
          const s = Object.assign({}, this.state, { maxReached: this.maxReached });
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
        } catch (e) { /* modo privado o cuota llena: seguir sin persistir */ }
      }

      restore() {
        let saved = null;
        try { saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY)); } catch (e) { return false; }
        if (!saved || typeof saved !== 'object') return false;

        Object.keys(this.state).forEach((k) => {
          if (k === 'step' || saved[k] === undefined) return;
          this.state[k] = saved[k];
        });
        if (!Array.isArray(this.state.restrictions)) this.state.restrictions = [];

        // Reflejar los valores guardados en los controles
        this.querySelectorAll('[data-nc-range]').forEach((r) => {
          const v = this.state[r.dataset.ncField];
          if (v == null) return;
          r.value = v;
          this.syncOutput(r.dataset.ncField, r.value);
        });
        this.querySelectorAll('[data-nc-field][data-nc-value]').forEach((btn) => {
          const f = btn.dataset.ncField, val = btn.dataset.ncValue;
          const cur = this.state[f];
          const on = Array.isArray(cur) ? cur.indexOf(val) !== -1 : cur === val;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        this.maxReached = Math.max(0, Math.min(this.PLAN_STEP, parseInt(saved.maxReached, 10) || 0));
        const step = Math.max(0, Math.min(this.PLAN_STEP, parseInt(saved.step, 10) || 0));
        // Solo devolver al plan si sus datos siguen completos
        if (step === this.PLAN_STEP && !this.canBuildPlan()) return false;
        this._restoredStep = step;
        return true;
      }

      canBuildPlan() {
        const s = this.state;
        const trains = (parseFloat(s.frequency_per_week) || 0) > 0;
        return !!(s.sex && s.weight_kg && s.height_cm && s.age && s.activity && s.experience && s.goal
          && (!trains || (s.duration && s.training_type)));
      }

      syncOutput(field, value) {
        const out = this.querySelector('[data-nc-output="' + this.outputKey(field) + '"]');
        if (out) out.textContent = value;
      }

      outputKey(field) {
        return { age: 'age', weight_kg: 'weight', height_cm: 'height', frequency_per_week: 'frequency' }[field] || field;
      }

      // Con 0 días de entreno, duración y tipo dejan de aplicar: se ocultan y se limpian
      toggleTrainingFields() {
        const trains = (parseFloat(this.state.frequency_per_week) || 0) > 0;
        ['duration', 'training_type'].forEach((field) => {
          const el = this.querySelector('[data-nc-field="' + field + '"]');
          const group = el && el.closest('.nutrition-calc__field');
          if (!group) return;
          group.hidden = !trains;
          if (!trains && this.state[field] != null) {
            this.state[field] = null;
            this.querySelectorAll('[data-nc-field="' + field + '"]').forEach((b) => {
              b.classList.remove('is-selected');
              b.setAttribute('aria-pressed', 'false');
            });
            this.clearInvalid(field);
          }
        });
      }

      updateLiveStats() {
        const w = this.state.weight_kg, h = this.state.height_cm;
        if (!w || !h) return;
        const bmi = this.round1(w / Math.pow(h / 100, 2));
        const bmiOut = this.querySelector('[data-nc-output="bmi"]');
        if (bmiOut) bmiOut.textContent = bmi.toFixed(1);
        const note = this.querySelector('[data-nc-imc-note]');
        if (note) note.textContent = this.bmiBand(bmi) + ' · solo es una referencia inicial.';
      }

      bmiBand(bmi) {
        if (bmi < 18.5) return 'Bajo peso';
        if (bmi < 25) return 'Peso saludable';
        if (bmi < 30) return 'Sobrepeso';
        return 'Obesidad';
      }

      /* ---------- navegación ---------- */
      onTab(i) { if (i <= this.maxReached) this.goTo(i); }

      onNext() {
        if (this.state.step >= this.PLAN_STEP) return;
        if (!this.validate(this.state.step)) return;
        this.goTo(this.state.step + 1);
      }

      goTo(i, opts) {
        const focus = !(opts && opts.focus === false);
        i = Math.max(0, Math.min(this.PLAN_STEP, i));
        this.state.step = i;
        this.maxReached = Math.max(this.maxReached, i);

        const stepsEl = this.querySelector('.nutrition-calc__steps');
        const fromH = stepsEl ? stepsEl.offsetHeight : 0;

        this.steps.forEach((s) => {
          const on = parseInt(s.dataset.ncStep, 10) === i;
          s.classList.toggle('is-active', on);
          s.hidden = !on;
        });
        const atEnd = i === this.PLAN_STEP; // al llegar al plan, todo queda completado
        this.tabs.forEach((t) => {
          const idx = parseInt(t.dataset.ncTab, 10);
          t.classList.toggle('is-active', !atEnd && idx === i);
          t.classList.toggle('is-done', atEnd || (idx !== i && idx <= this.maxReached));
          if (idx === i) t.setAttribute('aria-current', 'step'); else t.removeAttribute('aria-current');
          t.setAttribute('aria-disabled', idx > this.maxReached ? 'true' : 'false');
        });
        if (this.progress) this.progress.style.width = (i / (this.TOTAL - 1)) * 100 + '%';

        this.prevBtn.hidden = i === 0;
        this.nextBtn.hidden = i === this.PLAN_STEP;
        if (this.nextLabel) this.nextLabel.textContent = i === this.PLAN_STEP - 1 ? 'Ver mi plan' : 'Continuar';

        if (i === this.PLAN_STEP) this.renderPlan();
        this.persist();

        this.animateStepsHeight(stepsEl, fromH);

        const vp = this.querySelector('.nutrition-calc__viewport');
        if (vp) vp.scrollTop = 0;

        if (focus) {
          const active = this.steps[i];
          const heading = active && active.querySelector('h2');
          if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
          // En modal: desplaza solo el diálogo, no la página de fondo
          const modalDialog = this.closest('.nc-modal__dialog');
          if (modalDialog) {
            modalDialog.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (window.scrollTo) {
            window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
          }
        }
      }

      // Anima la altura del contenedor de pasos (de fromH a la altura natural del nuevo paso)
      animateStepsHeight(stepsEl, fromH) {
        if (!stepsEl) return;
        if (this._heightTimer) { clearTimeout(this._heightTimer); this._heightTimer = null; }
        if (this._heightDone) { stepsEl.removeEventListener('transitionend', this._heightDone); this._heightDone = null; }

        stepsEl.style.height = 'auto';
        const toH = stepsEl.offsetHeight;
        if (!fromH || Math.abs(fromH - toH) < 2) { stepsEl.style.height = ''; return; }

        stepsEl.style.height = fromH + 'px';
        void stepsEl.offsetWidth; // forzar reflow para que la transición arranque
        stepsEl.style.height = toH + 'px';

        const done = (e) => {
          if (e && e.propertyName && e.propertyName !== 'height') return;
          stepsEl.style.height = '';
          stepsEl.removeEventListener('transitionend', done);
          this._heightDone = null;
          if (this._heightTimer) { clearTimeout(this._heightTimer); this._heightTimer = null; }
        };
        this._heightDone = done;
        stepsEl.addEventListener('transitionend', done);
        this._heightTimer = setTimeout(done, 460);
      }

      /* ---------- validación ---------- */
      validate(step) {
        // Con 0 días de entrenamiento, duración y tipo no aplican (ver toggleTrainingFields)
        const trains = (parseFloat(this.state.frequency_per_week) || 0) > 0;
        const required = {
          0: ['sex'],
          1: ['weight_kg', 'height_cm'],
          2: trains ? ['activity', 'duration', 'training_type', 'experience'] : ['activity', 'experience'],
          3: ['goal'],
          4: [], // ESTILO: horario y restricciones son opcionales
        }[step] || [];
        let ok = true; let firstBad = null;
        required.forEach((f) => {
          const valid = Array.isArray(this.state[f]) ? this.state[f].length > 0 : this.state[f] != null;
          if (!valid) { ok = false; this.markInvalid(f); if (!firstBad) firstBad = f; }
        });
        if (!ok && firstBad) {
          const el = this.querySelector('[data-nc-field="' + firstBad + '"]');
          const group = el && el.closest('.nutrition-calc__field');
          (group || el)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return ok;
      }

      markInvalid(field) {
        const el = this.querySelector('[data-nc-field="' + field + '"]');
        const group = el && el.closest('.nutrition-calc__field');
        (group || el)?.classList.add('nc-invalid');
      }
      clearInvalid(field) {
        const el = this.querySelector('[data-nc-field="' + field + '"]');
        const group = el && el.closest('.nutrition-calc__field');
        (group || el)?.classList.remove('nc-invalid');
      }

      /* ---------- factor de actividad (puro) ---------- */
      // Piso por actividad cotidiana + carga de entreno con rendimiento decreciente.
      // Devuelve el factor crudo, el canónico más cercano y su etiqueta de nivel.
      static computeActivityFactor(state, config) {
        const base = config.activityBase[state.activity];
        const days = parseFloat(state.frequency_per_week) || 0;
        const mid = config.durationMid[state.duration] || 0;
        const weeklyMin = days * mid;
        const load = weeklyMin > 0 ? (config.loadA * weeklyMin) / (weeklyMin + config.loadC) : 0;
        const canon = config.canonFactors;
        const raw = Math.min(canon[canon.length - 1], (base != null ? base : config.activityBase.sentado) + load);

        let best = 0;
        for (let i = 1; i < canon.length; i++) {
          if (Math.abs(raw - canon[i]) < Math.abs(raw - canon[best])) best = i;
        }
        return {
          factor: canon[best],
          level: config.canonLevels[best],
          raw: Math.round(raw * 1000) / 1000,
          weeklyMin,
        };
      }

      /* ---------- cálculo (puro) ---------- */
      // Mifflin-St Jeor -> TDEE -> ajuste por objetivo -> macros -> hidratación/creatina.
      static computePlan(state, config) {
        const w = state.weight_kg, h = state.height_cm, age = state.age;
        const goal = state.goal, tt = state.training_type, exp = state.experience;

        // 1. TMB (Mifflin-St Jeor)
        const bmr = state.sex === 'female'
          ? (10 * w) + (6.25 * h) - (5 * age) - 161
          : (10 * w) + (6.25 * h) - (5 * age) + 5;

        // 2-3. Factor asignado por el algoritmo -> TDEE
        const act = this.computeActivityFactor(state, config);
        const tdee = bmr * act.factor;

        // 4. Ajuste por objetivo, con déficit/superávit acotados
        let kcalRaw = tdee, delta = 0;
        if (goal === 'perder_grasa') {
          delta = -Math.min(config.deficitMax, Math.max(config.deficitMin, tdee * config.deficitPct));
          kcalRaw = tdee + delta;
        } else if (goal === 'ganar_musculo') {
          const pct = config.surplusPct[exp] != null ? config.surplusPct[exp] : config.surplusPct.intermedio;
          delta = Math.min(config.surplusMax, Math.max(config.surplusMin, tdee * pct));
          kcalRaw = tdee + delta;
        }
        // Redondeo de calorías al múltiplo de 10 más cercano
        const r = config.kcalRoundTo || 10;
        const kcal = Math.max(0, Math.round(kcalRaw / r) * r);

        // 5. Proteína (g/kg) según objetivo, tipo de entreno y experiencia
        let proteinPerKg;
        if (goal === 'perder_grasa') {
          proteinPerKg = config.proteinLose;
        } else if (goal === 'ganar_musculo') {
          proteinPerKg = config.proteinGain[exp] != null ? config.proteinGain[exp] : config.proteinGain.intermedio;
        } else {
          proteinPerKg = config.proteinMaintain[tt] != null ? config.proteinMaintain[tt] : config.proteinMaintain.cardio;
        }
        const protein_g = Math.round(proteinPerKg * w);

        // 6. Grasas fijas por kg · 7. Carbohidratos = calorías restantes
        const fats_g = Math.round(config.fatPerKg * w);
        const carbs_g = Math.max(0, Math.round((kcal - protein_g * 4 - fats_g * 9) / 4));

        // 8. Hidratación = base por kg + adicional por duración de sesión
        const trains = (parseFloat(state.frequency_per_week) || 0) > 0;
        const bonus = trains ? (config.hydrationBonusMl[state.duration] || 0) : 0;
        const hydration_l = Math.round((config.hydrationMlPerKg * w + bonus) / 100) / 10;

        // Creatina: solo con estímulo de fuerza (fuerza/mixto) o en volumen
        const needsCreatine = trains
          ? (config.creatineTypes.indexOf(tt) !== -1 || config.creatineGoals.indexOf(goal) !== -1)
          : config.creatineGoals.indexOf(goal) !== -1;
        const creatine_g = needsCreatine
          ? Math.min(config.creatineMax, Math.max(config.creatineMin, Math.round(config.creatinePerKg * w)))
          : 0;

        const bmi = Math.round((w / Math.pow(h / 100, 2)) * 10) / 10;

        // Avisos (reglas de validación del documento)
        const belowBmr = kcal < bmr;
        const gainWithoutStrength = goal === 'ganar_musculo' &&
          (!trains || config.creatineTypes.indexOf(tt) === -1);

        return {
          kcal, protein_g, carbs_g, fats_g, creatine_g, hydration_l, bmi,
          bmr: Math.round(bmr), tdee: Math.round(tdee), delta: Math.round(delta),
          factor: act.factor, level: act.level, factorRaw: act.raw, weeklyMin: act.weeklyMin,
          needsCreatine, belowBmr, gainWithoutStrength,
        };
      }

      // Día ideal personalizado: orden por horario, proteína repartida (~100%),
      // comidas/copys según objetivo y tipo de entrenamiento.
      buildDayPlan(plan) {
        const C = this.config;
        const goal = this.state.goal, tt = this.state.training_type;
        const trains = (parseFloat(this.state.frequency_per_week) || 0) > 0;

        // Reparto por franja según cuántas comidas hace la persona
        const meals = parseInt(this.state.meals, 10) || C.defaultMeals;
        const split = C.mealSplits[meals] || C.mealSplits[C.defaultMeals];
        const slots = C.mealOrder.filter((k) => split[k] > 0);

        // Dónde cae el suplemento post-entreno: si el escenario tiene franja
        // propia, ahí; si no, en la comida siguiente al entreno según su horario.
        const supplementSlot = trains ? this.postWorkoutSlot(slots) : null;

        const LABEL = {
          desayuno: 'Desayuno', media_manana: 'Media mañana', almuerzo: 'Almuerzo',
          media_tarde: 'Media tarde', cena: 'Cena', post: 'Post-entreno',
        };
        // Lenguaje llano: nada de jerga de suplementación ("whey", "electrolitos"),
        // porque mucha gente que usa la calculadora no la conoce.
        const FOOD = {
          desayuno: 'proteínas y carbohidratos',
          media_manana: 'algo ligero con proteínas',
          almuerzo: 'proteínas, carbohidratos, grasas buenas y vegetales',
          media_tarde: 'algo ligero con proteínas',
          cena: {
            perder_grasa: 'proteínas magras, grasas buenas y vegetales',
            ganar_musculo: 'proteínas, carbohidratos, grasas buenas y vegetales',
          }[goal] || 'proteínas, grasas buenas y vegetales',
          post: 'para recuperar después de entrenar',
        };
        const SUPP = {
          cardio: 'un batido de proteína con carbohidratos',
          deporte: 'un batido de proteína con carbohidratos y sales minerales',
        };

        return slots.map((k) => {
          const pct = split[k];
          const kcal = Math.round((plan.kcal * pct) / 100);
          const prot = Math.round((plan.protein_g * pct) / 100);
          let note = kcal.toLocaleString('en-US') + ' kcal · ' + prot + 'g de proteína';
          if (k === supplementSlot) {
            let s = SUPP[tt] || 'un batido de proteína';
            // En la franja propia el "cuándo" ya lo dice la etiqueta Post-entreno
            if (k !== 'post') s += ' después de entrenar';
            if (plan.creatine_g > 0) s += ', más ' + plan.creatine_g + 'g de creatina';
            note += ' · ' + s;
          } else {
            note += ' · ' + FOOD[k];
          }
          return { label: LABEL[k] + ' · ' + pct + '%', hint: note };
        });
      }

      // Franja donde va la recomendación post-entreno. Las tablas del cliente solo
      // traen franja "post" en el escenario de 6 comidas; en los demás se adjunta
      // a la comida que sigue al entrenamiento.
      postWorkoutSlot(slots) {
        if (slots.indexOf('post') !== -1) return 'post';
        const pref = {
          manana: ['desayuno', 'media_manana', 'almuerzo'],
          tarde: ['media_tarde', 'almuerzo', 'cena'],
          noche: ['cena', 'media_tarde'],
        }[this.state.schedule] || ['almuerzo', 'cena'];
        for (let i = 0; i < pref.length; i++) {
          if (slots.indexOf(pref[i]) !== -1) return pref[i];
        }
        return slots[slots.length - 1];
      }

      scheduleLabel(s) { return { manana: 'mañana', tarde: 'tarde', noche: 'noche' }[s] || ''; }

      /* ---------- recomendaciones ---------- */
      // Dos capas:
      //  · Filtro DURO: objetivo + restricciones (nunca recomendar algo incompatible).
      //  · Puntaje: los 6 ejes del mapa de productos (sexo, edad, IMC, nivel calculado,
      //    tipo de entreno, horario). Un eje vacío en el metafield = aplica a todos y no
      //    puntúa; así el catálogo sin curar sigue funcionando igual que antes.
      buildRecommendations(plan) {
        const goal = this.state.goal;
        const restr = this.state.restrictions;
        const eligible = this.products.filter((p) =>
          p.available !== false &&
          p.objetivo.indexOf(goal) !== -1 &&
          restr.every((r) => p.restricciones.indexOf(r) !== -1)
        );

        const axes = [
          ['sexo', this.sexKey(this.state.sex)],
          ['edad', this.ageBand(this.state.age)],
          ['imc', this.bmiBandKey(plan.bmi)],
          ['nivel', plan.level],
          ['tipo', this.state.training_type],
          ['horario', this.state.schedule],
        ];
        eligible.forEach((p) => {
          let score = 0;
          axes.forEach((ax) => {
            const list = p[ax[0]];
            if (Array.isArray(list) && list.length && ax[1] && list.indexOf(ax[1]) !== -1) score++;
          });
          p._score = score;
        });

        // Más coincidencias primero; a igual puntaje, mejor venta (prioridad) primero
        eligible.sort((a, b) => (b._score - a._score) || (a.prioridad - b.prioridad));
        const max = this.config.maxRecommendations;
        // 1ª pasada: máx 1 por categoría (mix variado), respetando el orden por ventas
        const out = []; const usedCat = {};
        for (let i = 0; i < eligible.length && out.length < max; i++) {
          const p = eligible[i];
          if (usedCat[p.categoria]) continue;
          usedCat[p.categoria] = true; out.push(p);
        }
        // 2ª pasada: si faltan, rellenar con el resto (permitiendo repetir categoría)
        for (let i = 0; i < eligible.length && out.length < max; i++) {
          if (out.indexOf(eligible[i]) === -1) out.push(eligible[i]);
        }
        return out;
      }

      /* ---------- render del plan ---------- */
      renderPlan() {
        const plan = this.constructor.computePlan(this.state, this.config);
        this._plan = plan;

        const set = (sel, txt) => { const el = this.querySelector(sel); if (el) el.textContent = txt; };
        set('[data-nc-kcal]', this.formatThousands(plan.kcal));
        const days = parseFloat(this.state.frequency_per_week) || 0;
        set('[data-nc-kcal-sub]', 'kcal / día · ' + (days === 0 ? 'sin entrenamientos' : days + (days === 1 ? ' día' : ' días') + ' de entreno'));
        set('[data-nc-protein]', plan.protein_g + 'g');
        set('[data-nc-carbs]', plan.carbs_g + 'g');
        set('[data-nc-fats]', plan.fats_g + 'g');
        set('[data-nc-creatine]', plan.creatine_g + 'g');
        set('[data-nc-hydration]', plan.hydration_l + 'L');
        set('[data-nc-summary]', this.planSummary(plan));

        // Trazabilidad del cálculo: TMB, factor asignado y TDEE
        set('[data-nc-bmr]', this.formatThousands(plan.bmr));
        set('[data-nc-factor]', this.cap(plan.level) + ' · ×' + plan.factor.toFixed(3));
        set('[data-nc-tdee]', this.formatThousands(plan.tdee));

        // La creatina solo aplica con estímulo de fuerza o en volumen
        const creatinePill = this.querySelector('[data-nc-creatine-pill]');
        if (creatinePill) creatinePill.hidden = !plan.needsCreatine;

        // Avisos (reglas de validación del documento)
        const bmrWarn = this.querySelector('[data-nc-bmr-warn]');
        if (bmrWarn) bmrWarn.hidden = !plan.belowBmr;
        const strengthWarn = this.querySelector('[data-nc-strength-warn]');
        if (strengthWarn) strengthWarn.hidden = !plan.gainWithoutStrength;

        // Status accesible (una línea)
        const status = this.querySelector('[data-nc-status]');
        if (status) status.textContent = 'Plan listo: ' + this.formatThousands(plan.kcal) + ' kcal, ' + plan.protein_g + 'g proteína.';

        // Día ideal
        const dp = this.querySelector('[data-nc-dayplan]');
        if (dp) {
          dp.innerHTML = this.buildDayPlan(plan).map((d) => (
            '<li class="nutrition-calc__dayplan-item"><span class="nutrition-calc__dayplan-slot">' +
            this.escape(d.label) + '</span><span class="nutrition-calc__dayplan-hint">' + this.escape(d.hint) + '</span></li>'
          )).join('');
        }

        // Recomendaciones
        const recs = this.buildRecommendations(plan);
        const recsList = this.querySelector('[data-nc-recs-list]');
        const empty = this.querySelector('[data-nc-recs-empty]');
        const recsWrap = this.querySelector('[data-nc-recs]');
        const buyAll = this.querySelector('[data-nc-buy-all]');
        this._recs = recs;
        if (recsWrap && recsList) {
          if (this.products.length === 0) {
            recsWrap.hidden = true; // sin colección configurada
          } else {
            recsWrap.hidden = false;
            // Cada recomendación es tarjeta + franja de variantes
            recsList.innerHTML = recs.map((p, i) => (
              '<div class="nutrition-calc__rec-group">' + this.recCard(p, i === 0) + this.variantBand(p) + '</div>'
            )).join('');
            if (empty) empty.hidden = recs.length > 0;
            const whyEl = this.querySelector('[data-nc-recs-why]');
            if (whyEl) whyEl.textContent = recs.length ? this.buildGeneralWhy(plan) : '';
          }
        }
        // "Comprar todo" -> checkout directo
        if (buyAll) buyAll.hidden = recs.length <= 1;
        this.updateBuyAllTotal();
      }

      // Total de "Agregar todo", según las variantes seleccionadas en ese momento
      updateBuyAllTotal() {
        const totalEl = this.querySelector('[data-nc-buy-all-total]');
        if (!totalEl) return;
        const total = (this._recs || []).reduce(
          (sum, p) => sum + (parseFloat(String(p.selected_price).replace(/[^0-9.]/g, '')) || 0), 0
        );
        totalEl.textContent = total > 0 ? '· S/ ' + total.toFixed(2) : '';
      }

      onBuyAll(btn) {
        const ids = (this._recs || []).map((p) => p.selected_variant_id).filter(Boolean);
        if (!ids.length || btn.classList.contains('loading')) return;
        btn.classList.add('loading');

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items: ids.map((id) => ({ id: parseInt(id, 10), quantity: 1 })) };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
          .then((res) => res.json())
          .then((response) => {
            btn.classList.remove('loading');
            if (response && response.status) return;
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) cartDrawer.classList.remove('is-empty');
            if (cart && typeof cart.renderContents === 'function') cart.renderContents(response);
            else if (cartDrawer) cartDrawer.classList.add('animate', 'active');
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nutrition-calculator', cartData: response });
            }
            btn.classList.add('added');
            setTimeout(() => btn.classList.remove('added'), 2000);
            this.closeHostModal();
          })
          .catch(() => btn.classList.remove('loading'));
      }

      // Si la calculadora vive dentro del modal del lanzador, ciérralo (para ver el cart-drawer)
      closeHostModal() {
        if (this.closest('.nc-modal__dialog')) {
          document.dispatchEvent(new CustomEvent('nutrition-calc:close-modal'));
        }
      }

      // Franja de variantes bajo la tarjeta, con el markup y las clases del PDP
      // (assets/component-product-variant-picker.css). La más cara viene marcada.
      variantBand(p) {
        if (!p.variants || p.variants.length < 2) return '';
        const name = 'nc-variant-' + p.id;
        const opts = p.variants.map((v, i) => {
          const id = name + '-' + i;
          const on = v.id === p.selected_variant_id;
          return (
            '<div class="variant-pill' + (on ? ' variant-pill--selected' : '') + '">' +
              '<input type="radio" id="' + id + '" name="' + name + '"' +
                ' value="' + v.id + '" data-nc-variant="' + p.id + '"' + (on ? ' checked' : '') + '>' +
              '<label for="' + id + '">' + this.escape(v.title) + ' · ' + this.escape(v.price) + '</label>' +
            '</div>'
          );
        }).join('');
        return (
          '<fieldset class="product-form__input product-form__input--pill nutrition-calc__variants">' +
            '<legend class="form__label">' + this.escape(p.option_name || 'Presentación') + '</legend>' +
            opts +
          '</fieldset>'
        );
      }

      // Cambio de variante: actualiza el precio mostrado y lo que se agrega al carrito
      onVariantChange(input) {
        const pid = input.dataset.ncVariant;
        const p = (this._recs || []).filter((x) => String(x.id) === String(pid))[0];
        if (!p) return;
        const v = p.variants.filter((x) => String(x.id) === String(input.value))[0];
        if (!v) return;
        p.selected_variant_id = v.id;
        p.selected_price = v.price;

        const wrap = input.closest('.nutrition-calc__rec-group');
        if (wrap) {
          const priceEl = wrap.querySelector('.cross-selling__price');
          if (priceEl) priceEl.textContent = v.price;
          const addBtn = wrap.querySelector('.cross-selling__add-btn');
          if (addBtn) addBtn.dataset.variantId = v.id;
          wrap.querySelectorAll('.variant-pill').forEach((pill) => {
            const radio = pill.querySelector('input[type="radio"]');
            pill.classList.toggle('variant-pill--selected', !!radio && radio.checked);
          });
        }
        this.updateBuyAllTotal();
      }

      recCard(p, isTop) {
        const brand = p.brand ? '<span class="cross-selling__brand">' + this.escape(p.brand) + '</span>' : '';
        const why = '<p class="nutrition-calc__rec-why">' + this.escape(this.productReason(p)) + '</p>';
        const top = isTop ? '<span class="nutrition-calc__top-match">TOP MATCH</span>' : '';
        const addIconHtml = this.addIcon
          ? '<img src="' + this.escape(this.addIcon) + '" alt="" class="cross-selling__add-icon" width="20" height="20">'
          : '<span class="svg-wrapper"><svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true" focusable="false"><path d="M8 4.57143H4.57143V8H3.42857V4.57143H0V3.42857H3.42857V0H4.57143V3.42857H8V4.57143Z" fill="currentColor"/></svg></span>';
        return (
          '<div class="cross-selling__card nutrition-calc__rec">' +
            top +
            '<a href="' + this.escape(p.url) + '" class="cross-selling__image-link" tabindex="-1" aria-hidden="true">' +
              '<img src="' + this.escape(p.image) + '" alt="" class="cross-selling__image" loading="lazy" width="72" height="72">' +
            '</a>' +
            '<div class="cross-selling__content">' +
              brand +
              '<a href="' + this.escape(p.url) + '" class="cross-selling__product-title">' + this.escape(p.title) + '</a>' +
              why +
              '<div class="cross-selling__footer">' +
                '<span class="cross-selling__price">' + this.escape(p.selected_price) + '</span>' +
                '<button type="button" class="cross-selling__add-btn" aria-label="' + this.escape('Agregar al carrito: ' + p.title) + '" data-variant-id="' + p.selected_variant_id + '">' +
                  addIconHtml +
                '</button>' +
              '</div>' +
            '</div>' +
          '</div>'
        );
      }

      /* ---------- add to cart (reusa contrato del cart-drawer) ---------- */
      onAddToCart(btn) {
        if (btn.disabled || btn.classList.contains('loading')) return;
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        btn.classList.add('loading');

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { id: parseInt(variantId, 10), quantity: 1 };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
          .then((res) => res.json())
          .then((response) => {
            btn.classList.remove('loading');
            if (response.status) { btn.classList.add('nc-error'); setTimeout(() => btn.classList.remove('nc-error'), 2000); return; }
            btn.classList.add('added');
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) cartDrawer.classList.remove('is-empty');
            if (cart && typeof cart.renderContents === 'function') cart.renderContents(response);
            else if (cartDrawer) cartDrawer.classList.add('animate', 'active');
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nutrition-calculator', productVariantId: variantId, cartData: response });
            }
            setTimeout(() => btn.classList.remove('added'), 2000);
            this.closeHostModal();
          })
          .catch(() => btn.classList.remove('loading'));
      }

      /* ---------- helpers ---------- */
      goalLabel(g) {
        return { perder_grasa: 'Perder grasa', mantener: 'Mantener', ganar_musculo: 'Ganar masa muscular' }[g] || '';
      }
      // Resumen del plan: cuerpo, objetivo, entreno y el ajuste aplicado sobre el TDEE
      planSummary(plan) {
        const parts = [this.state.weight_kg + ' kg', this.goalLabel(this.state.goal)];
        if (this.state.training_type) parts.push(this.cap(this.trainingLabel(this.state.training_type)));
        if (plan.delta < 0) parts.push('déficit de ' + this.formatThousands(-plan.delta) + ' kcal');
        else if (plan.delta > 0) parts.push('superávit de ' + this.formatThousands(plan.delta) + ' kcal');
        return parts.join(' · ');
      }
      // Bandas derivadas que alimentan el mapa de recomendaciones
      ageBand(age) { return age < 20 ? '15_19' : (age < 40 ? '20_39' : '40_mas'); }
      bmiBandKey(bmi) { return bmi < 20 ? 'bajo' : (bmi < 25 ? 'normal' : (bmi < 30 ? 'sobrepeso' : 'obesidad')); }
      sexKey(s) { return s === 'female' ? 'femenino' : 'masculino'; }
      trainingLabel(t) {
        return { fuerza: 'fuerza', cardio: 'cardio', mixto: 'mixto', deporte: 'deporte' }[t] || '';
      }
      restrLabel(r) {
        return { sin_lactosa: 'sin lactosa', vegano: 'vegano', bajo_azucar: 'bajo en azúcar', sensible_cafeina: 'sin cafeína' }[r] || '';
      }
      // "Por qué" general del set de productos (según objetivo + macros + restricciones)
      buildGeneralWhy(plan) {
        const goalTxt = {
          perder_grasa: 'perder grasa de forma controlada', mantener: 'mantener y tonificar tu físico',
          ganar_musculo: 'ganar masa muscular',
        }[this.state.goal] || 'tu objetivo';
        let s = 'Para ' + goalTxt + ', tu cuerpo necesita ~' + plan.protein_g + 'g de proteína y ' +
          this.formatThousands(plan.kcal) + ' kcal al día. Elegimos suplementos que ayudan a cubrir ese requerimiento y se ajustan a tu entrenamiento';
        const rs = this.state.restrictions.map((r) => this.restrLabel(r)).filter(Boolean);
        if (rs.length) s += ', respetando tus preferencias (' + rs.join(', ') + ')';
        return s + '.';
      }
      // "Por qué" de cada producto (según su categoría y tu requerimiento)
      productReason(p) {
        const plan = this._plan || {};
        const reasons = {
          creatina: 'Más fuerza, potencia y recuperación — útil en cualquier objetivo.',
          proteina: 'Te ayuda a alcanzar tus ' + (plan.protein_g || '') + 'g de proteína al día.',
          barra: 'Snack proteico práctico para tus comidas o pre-entreno.',
          aminoacidos: 'Apoya la recuperación y reduce el desgaste muscular.',
          pre_entreno: 'Energía y foco para tus sesiones de entrenamiento.',
          quemador: 'Acompaña tu déficit calórico para perder grasa.',
          colageno: 'Apoya articulaciones, piel y recuperación.',
          vitaminas: 'Cubre micronutrientes clave para tu energía y bienestar.',
        };
        return reasons[p.catRaw] || p.benefit || 'Recomendado para tu objetivo y preferencias.';
      }
      cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
      round1(n) { return Math.round(n * 10) / 10; }
      formatThousands(n) { return Math.round(n).toLocaleString('en-US'); }
      escape(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
          { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
        ));
      }
    }
  );
}
